import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { formatSizeLabel, formatSize } from "../../lib/utils";
import {
  SizeStockRow,
  PieceStockRow,
  ProcessItemStockRow,
  ProcessPartyStockRow,
  SizesStockFilter,
  PiecesStockFilter,
  ProcessItemsStockFilter,
  ProcessPartyStockFilter,
  SizeOptionProps,
  ItemOptionProps,
  ItemSizeOptionProps,
  PartyOptionProps,
} from "../../types/stock";

export interface StockState {
  sizesStock: SizeStockRow[];
  totalSizesStock: number;
  totalQtyIn: number;
  totalQtyOut: number;
  totalStock: number;
  sizesStockFilter: SizesStockFilter;

  piecesStock: PieceStockRow[];
  totalPiecesStock: number;
  totalPiecesInches: number;
  piecesStockFilter: PiecesStockFilter;

  processItemsStock: ProcessItemStockRow[];
  totalProcessItemsStock: number;
  processItemsStockFilter: ProcessItemsStockFilter;

  processPartyStock: ProcessPartyStockRow[];
  totalProcessPartyStock: number;
  processPartyStockFilter: ProcessPartyStockFilter;

  sizeOptions: SizeOptionProps[];
  itemOptions: ItemOptionProps[];
  itemSizeOptions: ItemSizeOptionProps[];
  partyOptions: PartyOptionProps[];

  loading: boolean;
  error: string | null;
}

const initialState: StockState = {
  sizesStock: [],
  totalSizesStock: 0,
  totalQtyIn: 0,
  totalQtyOut: 0,
  totalStock: 0,
  sizesStockFilter: {
    size_id: null,
    search: null,
    sortBy: "material_name",
    sortOrder: "asc",
  },

  piecesStock: [],
  totalPiecesStock: 0,
  totalPiecesInches: 0,
  piecesStockFilter: {
    size_id: null,
    party_id: null,
    sortBy: "party_name",
    sortOrder: "asc",
  },

  processItemsStock: [],
  totalProcessItemsStock: 0,
  processItemsStockFilter: {
    process_definition_id: null,
    item_id: null,
    item_size_id: null,
    sortBy: "item_name",
    sortOrder: "asc",
  },

  processPartyStock: [],
  totalProcessPartyStock: 0,
  processPartyStockFilter: {
    process_definition_id: null,
    party_id: null,
    item_id: null,
    item_size_id: null,
    sortBy: "wip",
    sortOrder: "desc",
  },

  sizeOptions: [],
  itemOptions: [],
  itemSizeOptions: [],
  partyOptions: [],

  loading: false,
  error: null,
};

type FetchSizesStockProps = {
  page: number;
  size?: number;
  size_id?: number | null;
  search?: string | null;
  sortBy?: string;
  sortOrder?: string;
};

type FetchPiecesStockProps = {
  page: number;
  size?: number;
  size_id?: number | null;
  party_id?: number | null;
  sortBy?: string;
  sortOrder?: string;
};

type FetchProcessItemsStockProps = {
  page: number;
  size?: number;
  process_definition_id?: number | null;
  item_id?: number | null;
  item_size_id?: number | null;
  sortBy?: string;
  sortOrder?: string;
};

type FetchProcessPartyStockProps = {
  page: number;
  size?: number;
  process_definition_id?: number | null;
  party_id?: number | null;
  item_id?: number | null;
  item_size_id?: number | null;
  sortBy?: string;
  sortOrder?: string;
};

export const fetchSizesStock = createAsyncThunk(
  "stock/fetchSizesStock",
  async (req: FetchSizesStockProps) => {
    let response: any;
    await GET(`/v1/stock/sizes`, { ...req })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchStockSizes = createAsyncThunk(
  "stock/fetchSizes",
  async () => {
    let response: any;
    await GET(`/v1/stock-sizes`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchStockItems = createAsyncThunk(
  "stock/fetchItems",
  async () => {
    let response: any;
    await GET(`/v1/stock-items`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchStockItemSizes = createAsyncThunk(
  "stock/fetchItemSizes",
  async () => {
    let response: any;
    await GET(`/v1/stock-item-sizes`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchStockParties = createAsyncThunk(
  "stock/fetchParties",
  async () => {
    let response: any;
    await GET(`/v1/stock-parties`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchPiecesStock = createAsyncThunk(
  "stock/fetchPiecesStock",
  async (req: FetchPiecesStockProps) => {
    let response: any;
    await GET(`/v1/stock/pieces`, { ...req })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessItemsStock = createAsyncThunk(
  "stock/fetchProcessItemsStock",
  async (req: FetchProcessItemsStockProps) => {
    let response: any;
    await GET(`/v1/stock/process-items`, { ...req })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessPartyStock = createAsyncThunk(
  "stock/fetchProcessPartyStock",
  async (req: FetchProcessPartyStockProps) => {
    let response: any;
    await GET(`/v1/stock/process-parties`, { ...req })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const stockSlice = createSlice({
  name: "stock",
  initialState,
  reducers: {
    setSizesStockFilter: (state, action: PayloadAction<Partial<SizesStockFilter>>) => {
      state.sizesStockFilter = { ...state.sizesStockFilter, ...action.payload };
    },
    setPiecesStockFilter: (state, action: PayloadAction<Partial<PiecesStockFilter>>) => {
      state.piecesStockFilter = { ...state.piecesStockFilter, ...action.payload };
    },
    setProcessItemsStockFilter: (state, action: PayloadAction<Partial<ProcessItemsStockFilter>>) => {
      state.processItemsStockFilter = { ...state.processItemsStockFilter, ...action.payload };
    },
    setProcessPartyStockFilter: (state, action: PayloadAction<Partial<ProcessPartyStockFilter>>) => {
      state.processPartyStockFilter = { ...state.processPartyStockFilter, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSizesStock.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSizesStock.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.sizesStock = action.payload.data.data.records || [];
        state.totalSizesStock = action.payload.data.data.total_records || 0;
        state.totalQtyIn = action.payload.data.data.total_qty_in || 0;
        state.totalQtyOut = action.payload.data.data.total_qty_out || 0;
        state.totalStock = action.payload.data.data.total_stock || 0;
        state.error = null;
      } else {
        state.sizesStock = [];
        state.error = action?.payload?.data?.message || "Something went wrong.";
      }
    });
    builder.addCase(fetchSizesStock.rejected, (state, action: any) => {
      state.loading = false;
      state.sizesStock = [];
      state.error = action?.payload?.data?.message || "Something went wrong.";
    });

    builder.addCase(fetchStockSizes.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.sizeOptions = (action.payload.data.data || []).map((s: any) => ({
          value: s.id,
          // s is now a MaterialSize row: { id, material_id, size, material: { name } }
          label: formatSizeLabel({ size: s.size, material_name: s.material?.name }),
        }));
      }
    });
    builder.addCase(fetchStockItems.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.itemOptions = (action.payload.data.data || []).map((i: any) => ({
          value: i.id,
          label: i.name,
        }));
      }
    });
    builder.addCase(fetchStockItemSizes.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.itemSizeOptions = (action.payload.data.data || []).map((s: any) => ({
          value: s.id,
          label: formatSize(s.size),
        }));
      }
    });
    builder.addCase(fetchStockParties.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.partyOptions = (action.payload.data.data || []).map((p: any) => ({
          value: p.id,
          label: p.firm_name || p.name,
        }));
      }
    });

    builder.addCase(fetchPiecesStock.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPiecesStock.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.piecesStock = action.payload.data.data.records || [];
        state.totalPiecesStock = action.payload.data.data.total_records || 0;
        state.totalPiecesInches = action.payload.data.data.total_inches || 0;
        state.error = null;
      } else {
        state.piecesStock = [];
        state.error = action?.payload?.data?.message || "Something went wrong.";
      }
    });
    builder.addCase(fetchPiecesStock.rejected, (state, action: any) => {
      state.loading = false;
      state.piecesStock = [];
      state.error = action?.payload?.data?.message || "Something went wrong.";
    });

    builder.addCase(fetchProcessItemsStock.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProcessItemsStock.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.processItemsStock = action.payload.data.data.records || [];
        state.totalProcessItemsStock = action.payload.data.data.total_records || 0;
        state.error = null;
      } else {
        state.processItemsStock = [];
        state.error = action?.payload?.data?.message || "Something went wrong.";
      }
    });
    builder.addCase(fetchProcessItemsStock.rejected, (state, action: any) => {
      state.loading = false;
      state.processItemsStock = [];
      state.error = action?.payload?.data?.message || "Something went wrong.";
    });

    builder.addCase(fetchProcessPartyStock.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProcessPartyStock.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.processPartyStock = action.payload.data.data.records || [];
        state.totalProcessPartyStock = action.payload.data.data.total_records || 0;
        state.error = null;
      } else {
        state.processPartyStock = [];
        state.error = action?.payload?.data?.message || "Something went wrong.";
      }
    });
    builder.addCase(fetchProcessPartyStock.rejected, (state, action: any) => {
      state.loading = false;
      state.processPartyStock = [];
      state.error = action?.payload?.data?.message || "Something went wrong.";
    });
  },
});

export const {
  setSizesStockFilter,
  setPiecesStockFilter,
  setProcessItemsStockFilter,
  setProcessPartyStockFilter,
} = stockSlice.actions;

export const getSizesStock = (state: RootState) => state.stock.sizesStock;
export const getTotalSizesStock = (state: RootState) => state.stock.totalSizesStock;
export const getTotalQtyIn = (state: RootState) => state.stock.totalQtyIn;
export const getTotalQtyOut = (state: RootState) => state.stock.totalQtyOut;
export const getTotalStock = (state: RootState) => state.stock.totalStock;
export const getSizesStockFilter = (state: RootState) => state.stock.sizesStockFilter;

export const getPiecesStock = (state: RootState) => state.stock.piecesStock;
export const getTotalPiecesStock = (state: RootState) => state.stock.totalPiecesStock;
export const getTotalPiecesInches = (state: RootState) => state.stock.totalPiecesInches;
export const getPiecesStockFilter = (state: RootState) => state.stock.piecesStockFilter;

export const getProcessItemsStock = (state: RootState) => state.stock.processItemsStock;
export const getTotalProcessItemsStock = (state: RootState) => state.stock.totalProcessItemsStock;
export const getProcessItemsStockFilter = (state: RootState) => state.stock.processItemsStockFilter;

export const getProcessPartyStock = (state: RootState) => state.stock.processPartyStock;
export const getTotalProcessPartyStock = (state: RootState) => state.stock.totalProcessPartyStock;
export const getProcessPartyStockFilter = (state: RootState) => state.stock.processPartyStockFilter;

export const getStockSizeOptions = (state: RootState) => state.stock.sizeOptions;
export const getStockItemOptions = (state: RootState) => state.stock.itemOptions;
export const getStockItemSizeOptions = (state: RootState) => state.stock.itemSizeOptions;
export const getStockPartyOptions = (state: RootState) => state.stock.partyOptions;
export const getStockLoading = (state: RootState) => state.stock.loading;

export default stockSlice.reducer;
